"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis } from "recharts";
import { Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
const socialLinks = [{
  name: "Twitter",
  icon: Twitter,
  url: "https://twitter.com"
}, {
  name: "Instagram",
  icon: Instagram,
  url: "https://instagram.com"
}, {
  name: "Facebook",
  icon: Facebook,
  url: "https://facebook.com"
}];
const legalLinks = [{
  name: "Privacy Policy",
  url: "/privacy"
}, {
  name: "Terms of Service",
  url: "/terms"
}];
const chartData = [{
  year: "2021",
  impact: 45
}, {
  year: "2022",
  impact: 80
}, {
  year: "2023",
  impact: 150
}, {
  year: "2024",
  impact: 210
}];
const chartConfig = {
  impact: {
    label: "Eco Impact Score",
    color: "var(--chart-1)"
  }
};
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-4">
        {[1, 2, 3].map((_, i) => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
          </li>)}
      </ul>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-4">
        <li data-index={0}>
          <Link to="/ShopPage" className="text-muted-foreground hover:text-primary transition-colors font-medium">Shop</Link>
        </li>
        <li data-index={1}>
          <Link to="/CustomTeamHubPage" className="text-muted-foreground hover:text-primary transition-colors font-medium">Custom</Link>
        </li>
        <li data-index={2}>
          <Link to="/EcoFabricStoryPage" className="text-muted-foreground hover:text-primary transition-colors font-medium">Fabric</Link>
        </li>
      </ul>;
  }
  return <ul className="space-y-4">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors font-medium">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function EcoThreadCommunityFooter() {
  return <section id="ecothread-community-footer" className="bg-background text-foreground pt-20 pb-8 border-t border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          
          {}
          <div className="flex flex-col items-start">
            <Link to="/" className="mb-6 inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1610560/img/ecothread_logo_footer.png" alt="EcoThread Logo" className="h-7" />
            </Link>
            <p className="text-muted-foreground leading-relaxed mb-6">
              EcoThread is an online shop for eco-friendly clothing & accessories, Custom Sports Wear for kids, parents and fans. Founded to combine sustainability with vibrant performance style.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>123 Greenway Drive, EcoCity, EC 45678</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="size-4 text-primary shrink-0" />
                <span>(123) 456-7890</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="size-4 text-primary shrink-0" />
                <span>contact@ecothread.com</span>
              </div>
            </div>
          </div>

          {}
          <div>
            <h3 className="font-sans font-bold text-lg tracking-tight mb-6 uppercase">Quick Links</h3>
            <MenuProvider menu_id="25">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div>
            <h3 className="font-sans font-bold text-lg tracking-tight mb-6 uppercase">Community Impact</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Track our growing sustainability metrics. Every purchase contributes to a greener future.
            </p>
            <div className="bg-card/50 p-4 border border-border">
              <ChartContainer config={chartConfig} className="h-[120px] w-full">
                <BarChart data={chartData} margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0
              }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
                  <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} fontSize={12} tick={{
                  fill: 'var(--muted-foreground)'
                }} />
                  <ChartTooltip cursor={{
                  fill: 'var(--muted)',
                  opacity: 0.2
                }} content={<ChartTooltipContent hideLabel />} />
                  <Bar dataKey="impact" fill="var(--color-impact)" radius={0} />
                </BarChart>
              </ChartContainer>
            </div>
            <Link to="/CommunityFansPage" className="inline-block mt-4 text-sm font-bold text-primary hover:underline underline-offset-4">
              View Community Gallery &rarr;
            </Link>
          </div>

          {}
          <div>
            <h3 className="font-sans font-bold text-lg tracking-tight mb-6 uppercase">Connect</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Join the conversation and share your vibrant performance style with us.
            </p>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social, i) => <div key={social.name} data-index={i}>
                  <Link to={social.url} newTab className="flex items-center justify-center size-10 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm hover:shadow-md" aria-label={`Follow us on ${social.name}`}>
                    <social.icon className="size-5" />
                  </Link>
                </div>)}
            </div>
          </div>

        </div>

        {}
        <Separator className="mb-6" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
            &copy; {new Date().getFullYear()} EcoThread. All rights reserved.
          </p>
          <ul className="flex items-center gap-6">
            {legalLinks.map((link, i) => <li key={link.name} data-index={i}>
                <Link to={link.url} className="text-xs text-muted-foreground hover:text-foreground font-mono uppercase tracking-wider transition-colors">
                  {link.name}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}